import { useQuery } from '@tanstack/react-query';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/series-label';
import HighchartsReact from 'highcharts-react-official';
import type { TFunction } from 'i18next';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MappedAssetSensor } from '@/common/asset-sensor';
import type { TpsSensorAlarmRecord } from '@/common/sensors/tps/tps-timeseries-chart';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/context/ThemeContext';
import { HIGH_CHART_DARK_THEME, HIGH_CHART_LIGHT_THEME } from '@/lib/highchart/setup';
import { useQueryGetMetTimeSeriesChart } from '@/queries/assets/sensors/met/get-timeseries-chart';
import {
	type TpsSensorTimeSeriesChartFilters,
	useQueryGetTpsTimeSeriesChart,
} from '@/queries/assets/sensors/tps/get-timeseries-chart';
import { AbsoluteLoader } from '../../../absoluteLoader';
import { EmptyChart } from '../../EmptyChart';

function createPlotLines(
	value: number,
	color: string,
	labelText: string,
	dashStyle: Highcharts.DashStyleValue,
): Highcharts.YAxisPlotLinesOptions[] {
	return [
		{
			value,
			color,
			dashStyle,
			width: 1.8,
			label: {
				text: `${labelText} +`,
				align: 'right',
				style: { color },
			},
		},
		{
			value: -value,
			color,
			dashStyle,
			width: 1.8,
			label: {
				text: `${labelText} -`,
				style: { color },
				align: 'right',
			},
		},
	];
}

type AlarmTuple = [number | null, number | null, number | null];
function baseChartOptions(titleText: string, yAxisTitle: string, alarms: AlarmTuple, t: TFunction): Highcharts.Options {
	const [warnLow, warnHigh, alarm] = alarms;
	const plotLines = [
		...[warnLow ? createPlotLines(warnLow, '#9ca3af', t('health.charts.warnLow'), 'Dash') : []],
		...[warnHigh ? createPlotLines(warnHigh, '#ffbf00', t('health.charts.warnHigh'), 'ShortDash') : []],
		...[alarm ? createPlotLines(alarm, '#ff0000', t('health.charts.alarm'), 'ShortDot') : []],
	].flat();
	const minMaxValue = alarm || warnHigh || warnLow;

	const maxValue = minMaxValue ? Math.abs(minMaxValue) + 2 : undefined;
	const minValue = minMaxValue ? -(Math.abs(minMaxValue) + 2) : undefined;

	return {
		chart: {
			zooming: {
				type: 'x',
			},
			height: 300,
		},
		title: { text: titleText, style: { fontSize: '14px' } },
		xAxis: { type: 'datetime' },
		yAxis: [
			{ title: { text: yAxisTitle }, min: minValue, max: maxValue, plotLines },
			{ title: { text: t('health.charts.temperatureUnit') }, opposite: true },
		],
		legend: {
			enabled: false,
		},
		tooltip: { shared: true },
	};
}

export type TpsTimeSeriesChartsFilters = {
	startDate: Date | undefined;
	endDate: Date | undefined;
	meteoSensor: string;
	aggregation: TpsSensorTimeSeriesChartFilters['aggregation'] | 'none';
	warnLow: boolean;
	warnHigh: boolean;
	alarm: boolean;
	geglattet: boolean;
	delta: boolean;
};

export type TpsTimeSeriesChartsProps = {
	selectedSensor: MappedAssetSensor;
	assetId: string;
	filters: TpsTimeSeriesChartsFilters;
};

export function TpsTimeSeriesCharts({ selectedSensor, assetId, filters }: TpsTimeSeriesChartsProps) {
	const { t } = useTranslation();
	const { isDarkTheme } = useTheme();
	const chartFilters: TpsSensorTimeSeriesChartFilters = useMemo(
		() => ({
			from: filters.startDate?.toISOString(),
			to: filters.endDate?.toISOString(),
			aggregation: filters.aggregation !== 'none' ? filters.aggregation : undefined,
			smooth: filters.geglattet,
			delta: filters.delta,
		}),
		[filters.startDate, filters.endDate, filters.aggregation, filters.geglattet, filters.delta],
	);
	const meteoChartFilters = useMemo(
		() => ({
			from: filters.startDate?.toISOString(),
			to: filters.endDate?.toISOString(),
			aggregation: filters.aggregation !== 'none' ? filters.aggregation : undefined,
		}),
		[filters.startDate, filters.endDate, filters.aggregation],
	);
	const { data: chartResponse, isLoading } = useQuery(
		useQueryGetTpsTimeSeriesChart(assetId, selectedSensor.dataLoggerId, selectedSensor.id, chartFilters),
	);
	const { data: meteoChartResponse } = useQuery(
		useQueryGetMetTimeSeriesChart(assetId, filters.meteoSensor ? Number(filters.meteoSensor) : null, meteoChartFilters),
	);
	const tempData = meteoChartResponse?.chartData || [];
	const chartData = chartResponse?.chartData || [];
	const alarms = chartResponse?.alarms || ({} as TpsSensorAlarmRecord);
	const chartFinalData = useMemo(() => {
		const xChartData = chartData.map((record) => [new Date(record.measGroup).getTime(), record.x] as [number, number]);
		const yChartData = chartData.map((record) => [new Date(record.measGroup).getTime(), record.y] as [number, number]);
		const zChartData = chartData.map((record) => [new Date(record.measGroup).getTime(), record.z] as [number, number]);
		const tempSeriesData = tempData.map(
			(record) => [new Date(record.measGroup).getTime(), record.t] as [number, number],
		);

		// Seriennamen dynamisch nach Modus
		const nameX = filters.delta ? 'dX' : 'X';
		const nameY = filters.delta ? 'dY' : 'Y';
		const nameZ = filters.delta ? 'dZ' : 'Z';

		// alarms
		const xAlarms: AlarmTuple = [
			filters.warnLow ? alarms.warnLowX : null,
			filters.warnHigh ? alarms.warnHighX : null,
			filters.alarm ? alarms.alarmX : null,
		];
		const yAlarms: AlarmTuple = [
			filters.warnLow ? alarms.warnLowY : null,
			filters.warnHigh ? alarms.warnHighY : null,
			filters.alarm ? alarms.alarmY : null,
		];
		const zAlarms: AlarmTuple = [
			filters.warnLow ? alarms.warnLowZ : null,
			filters.warnHigh ? alarms.warnHighZ : null,
			filters.alarm ? alarms.alarmZ : null,
		];

		const yAxisTitle = filters.delta ? t('health.charts.change') : t('health.charts.coordinate');
		const tempSeries = {
			name: t('health.charts.temperature'),
			data: tempSeriesData,
			type: 'line',
			color: '#ff7f7f',
			yAxis: 1,
			tooltip: { valueSuffix: ' °C' },
			visible: tempSeriesData.length > 0,
			zIndex: 3,
		};

		return [
			{
				key: 'x',
				baseOptions: baseChartOptions(t('health.charts.xCoordinate'), yAxisTitle, xAlarms, t),
				series: [
					{ name: nameX, data: xChartData, type: 'line', zIndex: 5 },
					tempSeries,
				] as Highcharts.SeriesOptionsType[],
			},
			{
				key: 'y',
				baseOptions: baseChartOptions(t('health.charts.yCoordinate'), yAxisTitle, yAlarms, t),
				series: [{ name: nameY, data: yChartData, type: 'line', zIndex: 5 }, tempSeries],
			},
			{
				key: 'z',
				baseOptions: baseChartOptions(t('health.charts.zCoordinate'), yAxisTitle, zAlarms, t),
				series: [{ name: nameZ, data: zChartData, type: 'line', zIndex: 5 }, tempSeries],
			},
		];
	}, [chartData, filters.delta, alarms, filters.warnLow, filters.warnHigh, filters.alarm, tempData, t]);

	const charts = useMemo(
		() =>
			chartFinalData.map((data) => ({
				...data,
				baseOptions: merge(
					structuredClone(isDarkTheme ? HIGH_CHART_DARK_THEME : HIGH_CHART_LIGHT_THEME),
					data.baseOptions,
				),
			})),
		[chartFinalData, isDarkTheme],
	);
	if (isLoading) {
		return (
			<div className="h-50 relative w-full">
				<AbsoluteLoader className="bg-transparent" isLoading />
			</div>
		);
	}
	if (!chartData?.length) {
		return <EmptyChart title={t('health.charts.noDataTitle')} subtitle={t('health.charts.noDataSubtitle')} />;
	}
	return (
		<div className="space-y-3">
			{charts.map((chart) => (
				<Card key={chart.key} className="border-zinc-300 py-0">
					<CardContent className="py-1 px-1">
						<HighchartsReact
							highcharts={Highcharts}
							options={{
								...chart.baseOptions,
								series: chart.series,
							}}
						/>
					</CardContent>
				</Card>
			))}
		</div>
	);
}
